/**
 * TeamSystem Impact - ESG Organ Manager
 * Component for managing internal stakeholders (ESG governance organs)
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../../api/client';
import { useAuthStore } from '../../../store/authStore';
import { isReadOnlyRole } from '../../../utils/permissions';

// ESG Organ types
interface ESGOrganMember {
  id: string;
  name: string;
  email?: string;
  role: string;
  title?: string;
  isActive: boolean;
}

interface ESGOrgan {
  _id: string;
  type: string;
  name: string;
  description?: string;
  members: ESGOrganMember[];
  status: string;
  meetingFrequency?: string;
}

interface ESGOrganFormData {
  type: string;
  name: string;
  description: string;
  meetingFrequency: string;
}

// ESG Organ types
const ESG_ORGAN_TYPES = [
  { id: 'board', label: { it: 'Consiglio di Amministrazione', en: 'Board of Directors' } },
  { id: 'esg_committee', label: { it: 'Comitato ESG/Sostenibilità', en: 'ESG/Sustainability Committee' } },
  { id: 'esg_officer', label: { it: 'Responsabile ESG', en: 'ESG Officer' } },
  { id: 'esg_office', label: { it: 'Ufficio ESG', en: 'ESG Office' } },
  { id: 'esg_control', label: { it: 'Controllo ESG', en: 'ESG Control' } },
  { id: 'operating_system', label: { it: 'Sistema Operativo', en: 'Operating System' } },
];

// Member roles
const MEMBER_ROLES = [
  { id: 'chair', label: { it: 'Presidente', en: 'Chair' } },
  { id: 'vice_chair', label: { it: 'Vice Presidente', en: 'Vice Chair' } },
  { id: 'secretary', label: { it: 'Segretario', en: 'Secretary' } },
  { id: 'member', label: { it: 'Membro', en: 'Member' } },
  { id: 'observer', label: { it: 'Osservatore', en: 'Observer' } },
  { id: 'advisor', label: { it: 'Consulente', en: 'Advisor' } },
];

const MEETING_FREQUENCIES = [
  { id: 'weekly', label: { it: 'Settimanale', en: 'Weekly' } },
  { id: 'biweekly', label: { it: 'Bisettimanale', en: 'Biweekly' } },
  { id: 'monthly', label: { it: 'Mensile', en: 'Monthly' } },
  { id: 'quarterly', label: { it: 'Trimestrale', en: 'Quarterly' } },
  { id: 'annual', label: { it: 'Annuale', en: 'Annual' } },
  { id: 'as_needed', label: { it: 'A richiesta', en: 'As needed' } },
];

// I18n labels
function useLabels() {
  const { t } = useTranslation();

  return {
    internalStakeholders: t('dma.esgOrgan.internalStakeholders'),
    addOrgan: t('dma.esgOrgan.addOrgan'),
    editOrgan: t('dma.esgOrgan.editOrgan'),
    name: t('dma.esgOrgan.name'),
    type: t('dma.esgOrgan.type'),
    description: t('dma.esgOrgan.description'),
    members: t('dma.esgOrgan.members'),
    status: t('dma.esgOrgan.status'),
    meetingFrequency: t('dma.esgOrgan.meetingFrequency'),
    actions: t('dma.esgOrgan.actions'),
    save: t('dma.esgOrgan.save'),
    cancel: t('dma.esgOrgan.cancel'),
    delete: t('dma.esgOrgan.delete'),
    confirmDelete: t('dma.esgOrgan.confirmDelete'),
    noOrgans: t('dma.esgOrgan.noOrgans'),
    manageMembers: t('dma.esgOrgan.manageMembers'),
    addMember: t('dma.esgOrgan.addMember'),
    role: t('dma.esgOrgan.role'),
    email: 'Email',
    title: t('dma.esgOrgan.title'),
    activeMembers: t('dma.esgOrgan.activeMembers'),
    collapseOrgan: t('dma.esgOrgan.collapseOrgan'),
    expandOrgan: t('dma.esgOrgan.expandOrgan'),
    deleteOrgan: t('dma.esgOrgan.deleteOrgan'),
  };
}

const INITIAL_FORM: ESGOrganFormData = {
  type: 'esg_committee',
  name: '',
  description: '',
  meetingFrequency: 'monthly',
};

export function ESGOrganManager() {
  const labels = useLabels();
  const { i18n } = useTranslation();
  const lang = i18n.language as 'it' | 'en';
  const utente = useAuthStore((s) => s.utente);
  const isReadOnly = isReadOnlyRole(utente?.ruolo);
  const [organs, setOrgans] = useState<ESGOrgan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrgan, setExpandedOrgan] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrgan, setEditingOrgan] = useState<ESGOrgan | null>(null);
  const [formData, setFormData] = useState<ESGOrganFormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [organToDelete, setOrganToDelete] = useState<ESGOrgan | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Member dialog
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [memberFormData, setMemberFormData] = useState({
    name: '',
    email: '',
    role: 'member',
    title: '',
  });
  const [selectedOrganForMember, setSelectedOrganForMember] = useState<ESGOrgan | null>(null);

  useEffect(() => {
    loadOrgans();
  }, []);

  const loadOrgans = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/dma/governance/organs');
      setOrgans(response.data.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (organ?: ESGOrgan) => {
    if (organ) {
      setEditingOrgan(organ);
      setFormData({
        type: organ.type,
        name: organ.name,
        description: organ.description || '',
        meetingFrequency: organ.meetingFrequency || 'monthly',
      });
    } else {
      setEditingOrgan(null);
      setFormData(INITIAL_FORM);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingOrgan(null);
    setFormData(INITIAL_FORM);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const data = {
        type: formData.type,
        name: formData.name,
        description: formData.description || undefined,
        meetingFrequency: formData.meetingFrequency,
      };

      if (editingOrgan) {
        await apiClient.put(`/dma/governance/organs/${editingOrgan._id}`, data);
      } else {
        await apiClient.post('/dma/governance/organs', data);
      }

      handleCloseDialog();
      await loadOrgans();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!organToDelete) return;

    try {
      setDeleting(true);
      await apiClient.delete(`/dma/governance/organs/${organToDelete._id}`);
      setDeleteDialogOpen(false);
      setOrganToDelete(null);
      await loadOrgans();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedOrganForMember) return;

    try {
      setSaving(true);
      setError(null);

      await apiClient.post(`/dma/governance/organs/${selectedOrganForMember._id}/members`, {
        id: `member-${Date.now()}`,
        name: memberFormData.name,
        email: memberFormData.email || undefined,
        role: memberFormData.role,
        title: memberFormData.title || undefined,
        isActive: true,
        startDate: new Date().toISOString(),
      });

      setMemberDialogOpen(false);
      setMemberFormData({ name: '', email: '', role: 'member', title: '' });
      setSelectedOrganForMember(null);
      await loadOrgans();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getTypeLabel = (typeId: string) => {
    const type = ESG_ORGAN_TYPES.find(t => t.id === typeId);
    return type ? type.label[lang] || type.label.en : typeId;
  };

  const getRoleLabel = (roleId: string) => {
    const role = MEMBER_ROLES.find(r => r.id === roleId);
    return role ? role.label[lang] || role.label.en : roleId;
  };

  const getFrequencyLabel = (freqId: string) => {
    const freq = MEETING_FREQUENCIES.find(f => f.id === freqId);
    return freq ? freq.label[lang] || freq.label.en : freqId;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!isReadOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {labels.addOrgan}
          </Button>
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell>{labels.name}</TableCell>
              <TableCell>{labels.type}</TableCell>
              <TableCell align="center">{labels.members}</TableCell>
              <TableCell>{labels.meetingFrequency}</TableCell>
              <TableCell>{labels.status}</TableCell>
              <TableCell align="center">{labels.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{labels.noOrgans}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              organs.map(organ => (
                <>
                  <TableRow key={organ._id} hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setExpandedOrgan(expandedOrgan === organ._id ? null : organ._id)}
                        aria-label={expandedOrgan === organ._id ? labels.collapseOrgan : labels.expandOrgan}
                      >
                        {expandedOrgan === organ._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={500}>{organ.name}</Typography>
                      {organ.description && (
                        <Typography variant="caption" color="text.secondary">
                          {organ.description.substring(0, 50)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={getTypeLabel(organ.type)} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<PeopleIcon />}
                        label={organ.members.filter(m => m.isActive).length}
                        size="small"
                        color={organ.members.length > 0 ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {organ.meetingFrequency && getFrequencyLabel(organ.meetingFrequency)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={organ.status}
                        size="small"
                        color={getStatusColor(organ.status) as any}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {!isReadOnly && (
                        <>
                          <Tooltip title={labels.addMember}>
                            <IconButton
                              size="small"
                              aria-label={labels.addMember}
                              onClick={() => {
                                setSelectedOrganForMember(organ);
                                setMemberDialogOpen(true);
                              }}
                            >
                              <PersonIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={labels.editOrgan}>
                            <IconButton size="small" aria-label={labels.editOrgan} onClick={() => handleOpenDialog(organ)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={labels.delete}>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={labels.deleteOrgan}
                              onClick={() => {
                                setOrganToDelete(organ);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded row with members */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0 }}>
                      <Collapse in={expandedOrgan === organ._id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {labels.activeMembers} ({organ.members.filter(m => m.isActive).length})
                          </Typography>
                          <List dense>
                            {organ.members.filter(m => m.isActive).map(member => (
                              <ListItem key={member.id}>
                                <ListItemIcon>
                                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                    {member.name.charAt(0)}
                                  </Avatar>
                                </ListItemIcon>
                                <ListItemText
                                  primary={member.name}
                                  secondary={
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                      <Chip label={getRoleLabel(member.role)} size="small" variant="outlined" />
                                      {member.title && (
                                        <Typography variant="caption">{member.title}</Typography>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Organ Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingOrgan ? labels.editOrgan : labels.addOrgan}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{labels.type}</InputLabel>
              <Select
                value={formData.type}
                label={labels.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                {ESG_ORGAN_TYPES.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.label[lang] || type.label.en}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={labels.name}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label={labels.description}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <FormControl fullWidth>
              <InputLabel>{labels.meetingFrequency}</InputLabel>
              <Select
                value={formData.meetingFrequency}
                label={labels.meetingFrequency}
                onChange={(e) => setFormData({ ...formData, meetingFrequency: e.target.value })}
              >
                {MEETING_FREQUENCIES.map(freq => (
                  <MenuItem key={freq.id} value={freq.id}>
                    {freq.label[lang] || freq.label.en}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{labels.cancel}</Button>
          {!isReadOnly && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !formData.name}
            >
              {saving ? <CircularProgress size={20} /> : labels.save}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={memberDialogOpen} onClose={() => setMemberDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{labels.addMember}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={labels.name}
              value={memberFormData.name}
              onChange={(e) => setMemberFormData({ ...memberFormData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label={labels.email}
              type="email"
              value={memberFormData.email}
              onChange={(e) => setMemberFormData({ ...memberFormData, email: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>{labels.role}</InputLabel>
              <Select
                value={memberFormData.role}
                label={labels.role}
                onChange={(e) => setMemberFormData({ ...memberFormData, role: e.target.value })}
              >
                {MEMBER_ROLES.map(role => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.label[lang] || role.label.en}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={labels.title}
              value={memberFormData.title}
              onChange={(e) => setMemberFormData({ ...memberFormData, title: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberDialogOpen(false)}>{labels.cancel}</Button>
          {!isReadOnly && (
            <Button
              variant="contained"
              onClick={handleAddMember}
              disabled={saving || !memberFormData.name}
            >
              {saving ? <CircularProgress size={20} /> : labels.save}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{labels.delete}</DialogTitle>
        <DialogContent>
          <Typography>{labels.confirmDelete}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{labels.cancel}</Button>
          {!isReadOnly && (
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? <CircularProgress size={20} /> : labels.delete}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
